import { formatDollarAmount } from "@utils/costFormatter";

// Number of college years the pledged monthly amounts and the budget cover
const COLLEGE_YEARS = 4;
const MONTHS_IN_COLLEGE = COLLEGE_YEARS * 12;

/**
 * Format a dollar amount in compact form, e.g. 28200 -> "$28.2K"
 * @param {number} value
 */
export const formatCompactDollar = (value) => {
  const amount = Number(value) || 0;
  if (Math.abs(amount) < 1000) {
    return formatDollarAmount(amount);
  }
  const thousands = Math.round(Math.abs(amount) / 100) / 10;
  const sign = amount < 0 ? "-" : "";
  return `${sign}$${thousands % 1 === 0 ? thousands.toFixed(0) : thousands}K`;
};

/**
 * Read the federal SAI and the institutional SAI range from the scenario
 * @param {object} efcs - scenario.efcs
 */
export const getSaiValues = (efcs) => {
  const federal = Number(efcs?.federal_efc) || 0;
  const institutionalLower = Number(efcs?.institutional_lower_efc) || 0;
  const institutionalUpper =
    Number(efcs?.institutional_upper_efc) || institutionalLower;
  const isRange = institutionalLower !== institutionalUpper;

  // Ranges only fit the card in compact form, so both cards switch together
  const format = isRange ? formatCompactDollar : formatDollarAmount;

  return {
    federal,
    institutionalLower,
    institutionalUpper,
    isRange,
    federalLabel: format(federal),
    institutionalLabel: isRange
      ? `${format(institutionalLower)}–${format(institutionalUpper)}`
      : format(institutionalLower),
  };
};

/**
 * Split the family budget into its sources. Mirrors the yearly `yourBudget`
 * calculation in context, so `total / 4` always equals `yourBudget`.
 * @param {object} preapprovals - scenario.preapprovals
 */
export const getBudgetBreakdown = (preapprovals) => {
  const value = (key) => Number(preapprovals?.[key]) || 0;

  const amount529 = value("funds_in_529");
  const parentGuardian =
    value("parent_pledged_monthly") * MONTHS_IN_COLLEGE +
    value("parent_pledged_assets");
  const student =
    value("student_pledged_monthly") * MONTHS_IN_COLLEGE +
    value("student_pledged_assets_1");
  const grandparentOther = value("other_help");
  const total = amount529 + parentGuardian + student + grandparentOther;

  return {
    total,
    yearly: total / COLLEGE_YEARS,
    items: [
      { label: "529 Amount", value: amount529 },
      { label: "Parent / Guardian", value: parentGuardian },
      { label: "Student", value: student },
      { label: "Grandparent / Other", value: grandparentOther },
    ],
  };
};

/**
 * "What your SAIs mean" copy. Same text for every user, only the SAI values change.
 * @param {object} sai - result of getSaiValues
 */
export const getSaiMeaning = (sai) => {
  const federalLabel = formatCompactDollar(sai.federal);
  const institutionalLabel = sai.isRange
    ? `${formatCompactDollar(sai.institutionalLower)}–${formatCompactDollar(sai.institutionalUpper)}`
    : formatCompactDollar(sai.institutionalLower);

  return `Colleges use these numbers to decide how much aid you get based on financial need. The lower the number, the more aid you may qualify for. Most colleges use your Federal SAI (${federalLabel}). Some, mostly private, use your Institutional SAI (${institutionalLabel}).`;
};
